import HomologacionSINFields from '../common/HomologacionSINFields';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, ShoppingBag, CheckCircle2, ImageUp } from 'lucide-react';
import {
    createProductoCafeteria,
    ProductoCafeteria,
} from '../../services/productoCafeteriaService';
import { fetchSucursales, Sucursal } from '../../services/pos/sucursalService';
import { fetchCategoriasProducto, CategoriaProducto } from '../../services/categoriaProductoService';

interface AddProductsProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (p: ProductoCafeteria) => void;
}

const C = {
    primary: '#c1348b', primaryDark: '#8f1f64',
    secondary: '#61257b', secondaryDark: '#004d7a',
    secondaryLight: '#8b3fa8', accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.apm-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes apmFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes apmSlideUp{from{opacity:0;transform:translateY(20px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.apm-overlay{animation:apmFadeIn .22s ease both;}
.apm-dialog{animation:apmSlideUp .3s cubic-bezier(.16,1,.3,1) both;}
.apm-input, .apm-select{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid #e5e7eb;background:#fafafa;font-size:.88rem;color:#374151;outline:none;transition:all .2s;font-family:'DM Sans',sans-serif;resize:vertical;}
.apm-input:focus,.apm-select:focus{border-color:${C.secondary};box-shadow:0 0 0 3px rgba(97, 37, 123,.1);background:#fff;}
.apm-select{appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 12px center;}
.apm-file{width:100%;padding:10px 14px;border-radius:10px;border:1.5px dashed #d1d5db;background:#f8fafc;font-size:.85rem;color:#64748b;cursor:pointer;}
.apm-btn-primary{display:inline-flex;align-items:center;gap:6px;padding:10px 22px;border-radius:11px;border:none;background:linear-gradient(135deg,${C.primary},${C.primaryDark});color:#fff;font-weight:800;font-size:.88rem;cursor:pointer;box-shadow:0 4px 14px rgba(193, 52, 139,.28);transition:all .22s;}
.apm-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(193, 52, 139,.34);}
.apm-btn-primary:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.apm-btn-ghost{display:inline-flex;align-items:center;gap:6px;padding:10px 18px;border-radius:11px;border:1.5px solid #e2e8f0;background:#f8fafc;color:#374151;font-weight:700;font-size:.88rem;cursor:pointer;transition:all .2s;}
.apm-btn-ghost:hover{background:#f1f5f9;}
.apm-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.apm-full { grid-column: 1 / -1; }
`;

const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
    <div onClick={() => onChange(!checked)} style={{ width: '42px', height: '22px', borderRadius: '100px', background: checked ? C.secondary : '#d1d5db', position: 'relative', cursor: 'pointer', transition: 'background .25s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: '3px', left: checked ? '23px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left .25s' }} />
    </div>
);

const AddProducts: React.FC<AddProductsProps> = ({ isOpen, onClose, onCreated }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        precio: '',
        imagen: null as File | null,
        activo: true,
        sucursal: '' as string,
        categoria: '' as string,
        producto_servicio_sin: null as number | null, unidad_medida_sin: null as number | null,
        controlar_stock: false,
        stock: '' as string,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [categorias, setCategorias] = useState<CategoriaProducto[]>([]);

    useEffect(() => {
        if (isOpen) {
            setFormData({ nombre: '', descripcion: '', precio: '', imagen: null, activo: true, sucursal: '', categoria: '', producto_servicio_sin: null, unidad_medida_sin: null, controlar_stock: false, stock: '' });
            setError('');
            fetchSucursales()
                .then(data => setSucursales(data.filter(s => s.activo)))
                .catch(() => setError('Error al cargar sucursales'));
        }
    }, [isOpen]);

    useEffect(() => {
        if (formData.sucursal) {
            fetchCategoriasProducto(Number(formData.sucursal))
                .then(data => setCategorias(data))
                .catch(() => setError('Error al cargar categorías'));
            setFormData(prev => ({ ...prev, categoria: '' }));
        } else setCategorias([]);
    }, [formData.sucursal]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.sucursal || !formData.precio) {
            setError('Sucursal y precio son obligatorios');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const payload: any = {
                producto_servicio_sin: formData.producto_servicio_sin, unidad_medida_sin: formData.unidad_medida_sin,
                nombre: formData.nombre,
                descripcion: formData.descripcion,
                precio: formData.precio,
                activo: formData.activo,
                sucursal: Number(formData.sucursal),
                categoria: formData.categoria ? Number(formData.categoria) : null,
                controlar_stock: formData.controlar_stock,
                stock: formData.controlar_stock && formData.stock ? Number(formData.stock) : null,
                imagen: formData.imagen,
            };
            const newProd = await createProductoCafeteria(payload);
            onCreated(newProd);
            onClose();
        } catch {
            setError('Error al crear el producto.');
        } finally { setLoading(false); }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="apm-modal apm-overlay" style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(6px)' }}>
                <div className="apm-dialog" onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '22px', width: '100%', maxWidth: '700px', boxShadow: '0 32px 80px rgba(0,0,0,.2)', border: '1.5px solid #f1f5f9', overflow: 'hidden' }}>
                    <div style={{ height: '4px', background: `linear-gradient(90deg,${C.primary},${C.primaryDark},${C.accentDark},${C.primary}80)` }} />
                    <div style={{ padding: '20px 24px', borderBottom: '1.5px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: `linear-gradient(135deg,${C.primary},${C.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${C.primary}30` }}>
                                <Plus size={18} color="#fff" />
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1rem', fontWeight: '900', color: '#0f172a', letterSpacing: '-.02em' }}>Nuevo Producto</h3>
                                <p style={{ fontSize: '.72rem', color: '#94a3b8', marginTop: '1px' }}>Completa los datos del producto</p>
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width: '30px', height: '30px', borderRadius: '9px', border: '1.5px solid #f1f5f9', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'}
                            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'}>
                            <X size={14} color="#64748b" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <HomologacionSINFields className="apm-input apm-select" actividad={categorias.find(c => c.id === Number(formData.categoria))?.actividad_economica_codigo} producto={formData.producto_servicio_sin} unidad={formData.unidad_medida_sin} onChange={(producto, unidad) => setFormData({ ...formData, producto_servicio_sin: producto, unidad_medida_sin: unidad })} />
                        {error && <div style={{ display: 'flex', gap: '7px', padding: '9px 12px', borderRadius: '10px', background: `${C.primary}08`, border: `1.5px solid ${C.primary}22` }}><X size={12} color={C.primary} /><span style={{ fontSize: '.82rem', color: C.primaryDark, fontWeight: '600' }}>{error}</span></div>}

                        <div className="apm-grid">
                            {/* Nombre */}
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '.68rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '7px' }}><ShoppingBag size={10} color={C.secondary} /> Nombre *</label>
                                <input type="text" required value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} className="apm-input" placeholder="Ej: Café Americano" />
                            </div>
                            {/* Descripción */}
                            <div>
                                <label style={{ display: 'block', fontSize: '.68rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '7px' }}>Descripción</label>
                                <textarea value={formData.descripcion} onChange={e => setFormData({ ...formData, descripcion: e.target.value })} rows={2} className="apm-input" placeholder="Opcional" />
                            </div>
                            {/* Precio */}
                            <div>
                                <label style={{ display: 'block', fontSize: '.68rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '7px' }}>Precio (Bs.) *</label>
                                <input type="number" step="0.01" min="0" required value={formData.precio} onChange={e => setFormData({ ...formData, precio: e.target.value })} className="apm-input" placeholder="30.00" />
                            </div>
                            {/* Imagen */}
                            <div>
                                <label style={{ display: 'block', fontSize: '.68rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '7px' }}>Imagen</label>
                                <label className="apm-file" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <ImageUp size={16} color={C.secondary} />
                                    <span style={{ fontSize: '.82rem' }}>{formData.imagen ? formData.imagen.name : 'Seleccionar imagen'}</span>
                                    <input type="file" accept="image/*" onChange={e => setFormData({ ...formData, imagen: e.target.files?.[0] || null })} style={{ display: 'none' }} />
                                </label>
                            </div>
                            {/* Sucursal */}
                            <div>
                                <label style={{ display: 'block', fontSize: '.68rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '7px' }}>Sucursal *</label>
                                <select required value={formData.sucursal} onChange={e => setFormData({ ...formData, sucursal: e.target.value })} className="apm-select">
                                    <option value="">Seleccionar</option>
                                    {sucursales.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                                </select>
                            </div>
                            {/* Categoría */}
                            <div>
                                <label style={{ display: 'block', fontSize: '.68rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '7px' }}>Categoría</label>
                                <select value={formData.categoria} onChange={e => setFormData({ ...formData, categoria: e.target.value, producto_servicio_sin: null, unidad_medida_sin: null })} className="apm-select" disabled={!formData.sucursal}>
                                    <option value="">Sin categoría</option>
                                    {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Toggles y campos especiales en una sola fila */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            {/* Control de stock y su input */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#f8fafc', borderRadius: '12px', border: '1.5px solid #f1f5f9' }}>
                                    <div>
                                        <p style={{ fontSize: '.85rem', fontWeight: '700', color: '#374151' }}>Control de stock</p>
                                        <p style={{ fontSize: '.72rem', color: '#94a3b8', marginTop: '2px' }}>{formData.controlar_stock ? 'Se descontará automáticamente' : 'Stock ilimitado'}</p>
                                    </div>
                                    <Toggle checked={formData.controlar_stock} onChange={v => setFormData({ ...formData, controlar_stock: v })} />
                                </div>
                                {formData.controlar_stock && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: '.68rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '7px' }}>Cantidad inicial</label>
                                        <input type="number" min="0" value={formData.stock} onChange={e => setFormData({ ...formData, stock: e.target.value })} className="apm-input" placeholder="0" />
                                    </div>
                                )}
                            </div>

                            {/* Estado */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#f8fafc', borderRadius: '12px', border: '1.5px solid #f1f5f9', height: 'fit-content' }}>
                                <div>
                                    <p style={{ fontSize: '.85rem', fontWeight: '700', color: '#374151' }}>Estado del Producto</p>
                                    <p style={{ fontSize: '.72rem', color: '#94a3b8' }}>{formData.activo ? 'Visible y activo' : 'Oculto'}</p>
                                </div>
                                <Toggle checked={formData.activo} onChange={v => setFormData({ ...formData, activo: v })} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '4px', borderTop: '1.5px solid #f1f5f9', marginTop: '4px' }}>
                            <button type="button" onClick={onClose} className="apm-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={loading} className="apm-btn-primary">
                                {loading ? <><span style={{ width: '13px', height: '13px', border: '2px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block' }} /> Guardando...</> : <><CheckCircle2 size={14} /> Crear Producto</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>,
        document.body
    );
};

export default AddProducts;
